/**
 * RajaDashboard — Governance layer for Raja role holders.
 *
 * Five tabs:
 *  1. Domain Health   — negative-space score (STENA/KADYA/MADYAPA/AVIDVAN/SVAIRI)
 *  2. Approval Queue  — pending gigs, provider activations, home-visit bookings
 *  3. Disputes        — file, list, mediate
 *  4. Witness         — community witness queue (dambhahin, Rule 7)
 *  5. Impact Report   — Sahacharya aggregate across 12 Vedic dimensions
 *
 * ALL decisions are recorded by the human Raja — zero automation (Rule 4).
 */

import React, { useState, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import '../../styles/RajaDashboard.css';

const API_BASE = (import.meta.env.VITE_API_URL ?? '').replace(/\/api$/, '').replace(/\/$/, '');

async function rajaFetch<T>(
  path: string,
  token: string,
  options: RequestInit = {}
): Promise<T> {
  const res = await fetch(`${API_BASE}/api${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(options.headers ?? {}),
    },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error((err as { detail?: string }).detail ?? res.statusText);
  }
  return res.json() as Promise<T>;
}

// ── Types ────────────────────────────────────────────────────────────────────

interface DomainHealthIndicator { label: string; open_count: number; clear: boolean }
interface DomainHealthResponse {
  health_score: number;
  indicators: Record<string, DomainHealthIndicator>;
  window_days: number;
  computed_at: string;
}

interface ApprovalQueue {
  pending_gigs: GigItem[];
  pending_providers: ProviderItem[];
  pending_bookings: BookingItem[];
}

interface GigItem { id: string; title: string; dimension: string; provider_uid: string; created_at: string }
interface ProviderItem { uid: string; dimensions: string[]; submittedAt: string; portfolioUrl?: string }
interface BookingItem { id: string; gig_id: string; consumer_uid: string; provider_uid: string; created_at: string }

interface Dispute {
  id: string;
  booking_id: string;
  reporter_uid: string;
  reason_code: string;
  reason_label: string;
  dimension: string;
  description: string;
  status: string;
  mediation?: { decision: string; notes: string; remedy: string };
  created_at: string;
}

interface WitnessEntry {
  provider_uid: string;
  completed_bookings: number;
  witness_observations: number;
  passed_observations: number;
  witness_required: boolean;
}

interface DimImpact {
  complete: number; in_progress: number; recycled: number; total: number;
  open_disputes: number;
  marketplace_adhikara: { score: number | null; review_count: number };
}

// ── Shared status message component ─────────────────────────────────────────

function StatusMsg({ msg, type }: { msg: string; type: 'ok' | 'err' | 'info' }) {
  if (!msg) return null;
  return <div className={`rdash-status rdash-status--${type}`}>{msg}</div>;
}

// ── Tab: Domain Health ───────────────────────────────────────────────────────

function DomainHealthTab({ token }: { token: string }) {
  const [data, setData] = useState<DomainHealthResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const r = await rajaFetch<DomainHealthResponse>('/raja/domain-health', token);
      setData(r);
    } catch (e) { setErr((e as Error).message); }
    finally { setLoading(false); }
  }, [token]);

  React.useEffect(() => { load(); }, [load]);

  const scoreColor = (s: number) => s >= 0.8 ? '#34d399' : s >= 0.5 ? '#fbbf24' : '#f87171';

  return (
    <section className="rdash-section">
      <div className="rdash-section-header">
        <h2>राज्य-स्वास्थ्य — Domain Health</h2>
        <button className="rdash-btn rdash-btn--sm" onClick={load} disabled={loading}>
          {loading ? '…' : '↻ Refresh'}
        </button>
      </div>
      <StatusMsg msg={err} type="err" />
      {data && (
        <>
          <div className="rdash-health-score" style={{ color: scoreColor(data.health_score) }}>
            <span className="rdash-score-num">{Math.round(data.health_score * 100)}%</span>
            <span className="rdash-score-label">
              {data.health_score >= 0.8 ? 'Healthy' : data.health_score >= 0.5 ? 'Caution' : 'Alert'}
            </span>
          </div>
          <p className="rdash-meta">Rolling {data.window_days}-day window • Resolved disputes do not count</p>
          <div className="rdash-indicators-grid">
            {Object.entries(data.indicators).map(([code, ind]) => (
              <div key={code} className={`rdash-indicator rdash-indicator--${ind.clear ? 'clear' : 'flagged'}`}>
                <span className="rdash-indicator-code">{code}</span>
                <span className="rdash-indicator-label">{ind.label}</span>
                <span className="rdash-indicator-count">
                  {ind.clear ? '✓ Clear' : `⚠ ${ind.open_count} open`}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  );
}

// ── Tab: Approval Queue ──────────────────────────────────────────────────────

function ApprovalQueueTab({ token }: { token: string }) {
  const [queue, setQueue] = useState<ApprovalQueue | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [actionMsg, setActionMsg] = useState('');
  const [processing, setProcessing] = useState<string>('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const r = await rajaFetch<ApprovalQueue>('/raja/approval-queue', token);
      setQueue(r);
    } catch (e) { setErr((e as Error).message); }
    finally { setLoading(false); }
  }, [token]);

  React.useEffect(() => { load(); }, [load]);

  const decideProvider = async (uid: string, decision: 'approved' | 'rejected', notes: string) => {
    setProcessing(uid);
    try {
      await rajaFetch(`/raja/seva-provider-activation/${uid}/decision`, token, {
        method: 'POST', body: JSON.stringify({ decision, notes }),
      });
      setActionMsg(`Provider ${uid.slice(-6)} → ${decision}`);
      await load();
    } catch (e) { setActionMsg(`Error: ${(e as Error).message}`); }
    finally { setProcessing(''); }
  };

  const decideGig = async (gig_id: string, decision: 'ACTIVE' | 'SUSPENDED', notes: string) => {
    setProcessing(gig_id);
    try {
      await rajaFetch(`/raja/gigs/${gig_id}/decision`, token, {
        method: 'POST', body: JSON.stringify({ decision, notes }),
      });
      setActionMsg(`Gig ${gig_id.slice(-6)} → ${decision}`);
      await load();
    } catch (e) { setActionMsg(`Error: ${(e as Error).message}`); }
    finally { setProcessing(''); }
  };

  const decideBooking = async (booking_id: string, approved: boolean) => {
    const notes = window.prompt(approved ? 'Approval notes (optional):' : 'Rejection reason:') ?? '';
    setProcessing(booking_id);
    try {
      await rajaFetch(`/seva-gigs/bookings/${booking_id}/raja-approve`, token, {
        method: 'POST', body: JSON.stringify({ approved, notes }),
      });
      setActionMsg(`Booking ${booking_id.slice(-6)} → ${approved ? 'approved' : 'rejected'}`);
      await load();
    } catch (e) { setActionMsg(`Error: ${(e as Error).message}`); }
    finally { setProcessing(''); }
  };

  return (
    <section className="rdash-section">
      <div className="rdash-section-header">
        <h2>Approval Queue</h2>
        <button className="rdash-btn rdash-btn--sm" onClick={load} disabled={loading}>
          {loading ? '…' : '↻ Refresh'}
        </button>
      </div>
      <StatusMsg msg={err} type="err" />
      {actionMsg && <StatusMsg msg={actionMsg} type="ok" />}
      {queue && (
        <>
          {/* Pending Gigs */}
          <h3 className="rdash-queue-heading">
            Gig Listings ({queue.pending_gigs.length})
          </h3>
          {queue.pending_gigs.length === 0
            ? <p className="rdash-empty">No pending gig activations.</p>
            : queue.pending_gigs.map(g => (
              <div key={g.id} className="rdash-queue-item">
                <div className="rdash-queue-item-info">
                  <strong>{g.title}</strong>
                  <span className="rdash-badge">{g.dimension}</span>
                  <span className="rdash-meta">{g.provider_uid.slice(-8)}</span>
                </div>
                <div className="rdash-queue-actions">
                  <button
                    className="rdash-btn rdash-btn--approve"
                    disabled={processing === g.id}
                    onClick={() => decideGig(g.id, 'ACTIVE', '')}
                  >Activate</button>
                  <button
                    className="rdash-btn rdash-btn--reject"
                    disabled={processing === g.id}
                    onClick={() => {
                      const n = window.prompt('Suspension reason:') ?? '';
                      decideGig(g.id, 'SUSPENDED', n);
                    }}
                  >Suspend</button>
                </div>
              </div>
            ))
          }

          {/* Pending Provider Activations */}
          <h3 className="rdash-queue-heading">
            Provider Activations ({queue.pending_providers.length})
          </h3>
          {queue.pending_providers.length === 0
            ? <p className="rdash-empty">No pending provider activations.</p>
            : queue.pending_providers.map(p => (
              <div key={p.uid} className="rdash-queue-item">
                <div className="rdash-queue-item-info">
                  <strong>{p.uid.slice(-10)}</strong>
                  <span className="rdash-meta">{p.dimensions.join(', ')}</span>
                  {p.portfolioUrl && (
                    <a href={p.portfolioUrl} target="_blank" rel="noopener noreferrer" className="rdash-link">
                      Portfolio ↗
                    </a>
                  )}
                </div>
                <div className="rdash-queue-actions">
                  <button
                    className="rdash-btn rdash-btn--approve"
                    disabled={processing === p.uid}
                    onClick={() => {
                      const n = window.prompt('Approval notes:') ?? '';
                      decideProvider(p.uid, 'approved', n);
                    }}
                  >Approve</button>
                  <button
                    className="rdash-btn rdash-btn--reject"
                    disabled={processing === p.uid}
                    onClick={() => {
                      const n = window.prompt('Rejection reason:') ?? 'Does not meet criteria';
                      decideProvider(p.uid, 'rejected', n);
                    }}
                  >Reject</button>
                </div>
              </div>
            ))
          }

          {/* Pending Home-Visit Bookings */}
          <h3 className="rdash-queue-heading">
            Home-Visit Bookings ({queue.pending_bookings.length})
          </h3>
          {queue.pending_bookings.length === 0
            ? <p className="rdash-empty">No bookings awaiting approval.</p>
            : queue.pending_bookings.map(b => (
              <div key={b.id} className="rdash-queue-item">
                <div className="rdash-queue-item-info">
                  <strong>Booking {b.id.slice(-8)}</strong>
                  <span className="rdash-meta">Provider {b.provider_uid.slice(-8)} → Consumer {b.consumer_uid.slice(-8)}</span>
                </div>
                <div className="rdash-queue-actions">
                  <button
                    className="rdash-btn rdash-btn--approve"
                    disabled={processing === b.id}
                    onClick={() => decideBooking(b.id, true)}
                  >Approve</button>
                  <button
                    className="rdash-btn rdash-btn--reject"
                    disabled={processing === b.id}
                    onClick={() => decideBooking(b.id, false)}
                  >Reject</button>
                </div>
              </div>
            ))
          }
        </>
      )}
    </section>
  );
}

// ── Tab: Disputes ────────────────────────────────────────────────────────────

const REASON_CODES = ['STENA', 'KADYA', 'MADYAPA', 'AVIDVAN', 'SVAIRI'] as const;

function DisputesTab({ token }: { token: string }) {
  const [disputes, setDisputes] = useState<Dispute[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [actionMsg, setActionMsg] = useState('');
  const [mediating, setMediating] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const r = await rajaFetch<{ disputes: Dispute[] }>(
        `/raja/disputes${filterStatus ? `?status=${filterStatus}` : ''}`, token
      );
      setDisputes(r.disputes);
    } catch (e) { setErr((e as Error).message); }
    finally { setLoading(false); }
  }, [token, filterStatus]);

  React.useEffect(() => { load(); }, [load]);

  const mediate = async (dispute_id: string, decision: string) => {
    const notes = window.prompt(`Mediation notes for ${decision}:`) ?? '';
    const remedy = window.prompt('Remedy / action taken:') ?? '';
    setMediating(dispute_id);
    try {
      await rajaFetch(`/raja/disputes/${dispute_id}/mediate`, token, {
        method: 'POST', body: JSON.stringify({ decision, notes, remedy }),
      });
      setActionMsg(`Dispute ${dispute_id.slice(-6)} → ${decision}`);
      await load();
    } catch (e) { setActionMsg(`Error: ${(e as Error).message}`); }
    finally { setMediating(''); }
  };

  return (
    <section className="rdash-section">
      <div className="rdash-section-header">
        <h2>Disputes</h2>
        <div className="rdash-filter-row">
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="rdash-select">
            <option value="">All statuses</option>
            <option value="open">Open</option>
            <option value="resolved">Resolved</option>
            <option value="dismissed">Dismissed</option>
          </select>
          <button className="rdash-btn rdash-btn--sm" onClick={load} disabled={loading}>
            {loading ? '…' : '↻'}
          </button>
        </div>
      </div>
      <StatusMsg msg={err} type="err" />
      {actionMsg && <StatusMsg msg={actionMsg} type="ok" />}
      <p className="rdash-meta">
        Reason codes: {REASON_CODES.join(' • ')}. Disputes feed the domain health score.
      </p>
      {disputes.length === 0
        ? <p className="rdash-empty">No disputes found.</p>
        : disputes.map(d => (
          <div key={d.id} className={`rdash-dispute rdash-dispute--${d.status}`}>
            <div className="rdash-dispute-header">
              <span className="rdash-badge rdash-badge--reason">{d.reason_code}</span>
              <span className="rdash-badge rdash-badge--dim">{d.dimension}</span>
              <span className={`rdash-dispute-status rdash-dispute-status--${d.status}`}>{d.status}</span>
              <span className="rdash-meta">{new Date(d.created_at).toLocaleDateString()}</span>
            </div>
            <p className="rdash-dispute-desc">{d.description}</p>
            {d.mediation && (
              <div className="rdash-mediation-record">
                <strong>Mediation:</strong> {d.mediation.decision} — {d.mediation.notes}
                {d.mediation.remedy && <span> • Remedy: {d.mediation.remedy}</span>}
              </div>
            )}
            {d.status === 'open' && (
              <div className="rdash-queue-actions">
                <button
                  className="rdash-btn rdash-btn--approve"
                  disabled={mediating === d.id}
                  onClick={() => mediate(d.id, 'resolved')}
                >Resolve</button>
                <button
                  className="rdash-btn rdash-btn--neutral"
                  disabled={mediating === d.id}
                  onClick={() => mediate(d.id, 'dismissed')}
                >Dismiss</button>
                <button
                  className="rdash-btn rdash-btn--warn"
                  disabled={mediating === d.id}
                  onClick={() => mediate(d.id, 'escalated')}
                >Escalate</button>
              </div>
            )}
          </div>
        ))
      }
    </section>
  );
}

// ── Tab: Witness (Dambhahin — Rule 7) ────────────────────────────────────────

function WitnessTab({ token }: { token: string }) {
  const [queue, setQueue] = useState<WitnessEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [actionMsg, setActionMsg] = useState('');
  const [recording, setRecording] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const r = await rajaFetch<{ queue: WitnessEntry[] }>('/raja/witness-queue', token);
      setQueue(r.queue);
    } catch (e) { setErr((e as Error).message); }
    finally { setLoading(false); }
  }, [token]);

  React.useEffect(() => { load(); }, [load]);

  const recordWitness = async (provider_uid: string) => {
    const witnessId = window.prompt(
      'Community witness identity (name or UID — must NOT be you, the Raja):'
    );
    if (!witnessId?.trim()) return;
    const notes = window.prompt('Observation notes:') ?? '';
    const passedStr = window.prompt('Did the provider demonstrate dambhahin conduct? (yes/no)') ?? 'no';
    const passed = passedStr.toLowerCase().startsWith('y');

    setRecording(provider_uid);
    try {
      await rajaFetch(`/raja/witness/${provider_uid}/record`, token, {
        method: 'POST',
        body: JSON.stringify({
          passed,
          notes,
          community_witness_identity: witnessId.trim(),
        }),
      });
      setActionMsg(`Witness recorded for ${provider_uid.slice(-8)}: ${passed ? 'Passed ✓' : 'Not passed ✗'}`);
      await load();
    } catch (e) { setActionMsg(`Error: ${(e as Error).message}`); }
    finally { setRecording(''); }
  };

  return (
    <section className="rdash-section">
      <div className="rdash-section-header">
        <h2>Community Witness — दम्भहीन (Rule 7)</h2>
        <button className="rdash-btn rdash-btn--sm" onClick={load} disabled={loading}>
          {loading ? '…' : '↻ Refresh'}
        </button>
      </div>
      <StatusMsg msg={err} type="err" />
      {actionMsg && <StatusMsg msg={actionMsg} type="ok" />}
      <p className="rdash-meta">
        Providers with ≥3 completed bookings require 2 independent community witness confirmations.
        Self-declaration is forbidden. The Raja records the independent observer's identity.
      </p>
      {queue.length === 0
        ? <p className="rdash-empty">No providers require witness verification.</p>
        : queue.map(w => (
          <div key={w.provider_uid} className={`rdash-witness-item ${w.witness_required ? 'rdash-witness-item--required' : ''}`}>
            <div className="rdash-witness-info">
              <strong>{w.provider_uid.slice(-10)}</strong>
              <span className="rdash-meta">{w.completed_bookings} completed bookings</span>
              <span className={`rdash-badge ${w.passed_observations >= 2 ? 'rdash-badge--ok' : 'rdash-badge--warn'}`}>
                {w.passed_observations}/2 witness observations
              </span>
              {w.witness_required && (
                <span className="rdash-badge rdash-badge--alert">Witness required</span>
              )}
            </div>
            <button
              className="rdash-btn rdash-btn--primary"
              disabled={recording === w.provider_uid}
              onClick={() => recordWitness(w.provider_uid)}
            >
              {recording === w.provider_uid ? '…' : 'Record Observation'}
            </button>
          </div>
        ))
      }
    </section>
  );
}

// ── Tab: Impact Report ───────────────────────────────────────────────────────

const DIMENSION_LABELS: Record<string, string> = {
  food: 'भोजन', clothing: 'वस्त्र', housing: 'आवास', education: 'शिक्षा',
  health: 'स्वास्थ्य', transport: 'यातायात', festival: 'उत्सव', tradition: 'त्योहार',
  defense: 'रक्षा', service: 'सेवा', justice: 'न्याय', marriage: 'विवाह',
};

function ImpactReportTab({ token }: { token: string }) {
  const [report, setReport] = useState<Record<string, DimImpact> | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const r = await rajaFetch<{ dimensions: Record<string, DimImpact> }>('/raja/impact-report', token);
      setReport(r.dimensions);
    } catch (e) { setErr((e as Error).message); }
    finally { setLoading(false); }
  }, [token]);

  React.useEffect(() => { load(); }, [load]);

  return (
    <section className="rdash-section">
      <div className="rdash-section-header">
        <h2>Sahacharya Impact — 12 Dimensions</h2>
        <button className="rdash-btn rdash-btn--sm" onClick={load} disabled={loading}>
          {loading ? '…' : '↻ Refresh'}
        </button>
      </div>
      <StatusMsg msg={err} type="err" />
      {report && (
        <div className="rdash-impact-grid">
          {Object.entries(report).map(([dim, stats]) => {
            const label = DIMENSION_LABELS[dim] ?? dim;
            const adhikara = stats.marketplace_adhikara;
            return (
              <div key={dim} className="rdash-impact-card">
                <div className="rdash-impact-card-header">
                  <span className="rdash-impact-dim-hi">{label}</span>
                  <span className="rdash-impact-dim-en">{dim}</span>
                </div>
                <div className="rdash-impact-stats">
                  <span title="Completed">✓ {stats.complete}</span>
                  <span title="In progress">⏳ {stats.in_progress}</span>
                  <span title="Recycled">↩ {stats.recycled}</span>
                  {stats.open_disputes > 0 && (
                    <span title="Open disputes" className="rdash-impact-disputes">
                      ⚠ {stats.open_disputes}
                    </span>
                  )}
                </div>
                {adhikara.score !== null && (
                  <div className="rdash-impact-adhikara">
                    <span className="rdash-impact-adhikara-label">Marketplace Adhikara</span>
                    <span className="rdash-impact-adhikara-score">
                      {adhikara.score.toFixed(1)} / 9
                    </span>
                    <span className="rdash-meta">({adhikara.review_count} reviews)</span>
                  </div>
                )}
              </div>
            );
          })}
          {Object.keys(report).length === 0 && (
            <p className="rdash-empty">No gig activity recorded yet.</p>
          )}
        </div>
      )}
    </section>
  );
}

// ── Main Dashboard ───────────────────────────────────────────────────────────

type TabKey = 'health' | 'queue' | 'disputes' | 'witness' | 'impact';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'health',   label: '🏛 Domain Health' },
  { key: 'queue',    label: '⏳ Approval Queue' },
  { key: 'disputes', label: '⚖ Disputes' },
  { key: 'witness',  label: '👁 Witness' },
  { key: 'impact',   label: '📊 Impact Report' },
];

export default function RajaDashboard() {
  const { user, profile } = useAuth();
  const [tab, setTab] = useState<TabKey>('health');
  const [token, setToken] = useState<string>('');

  React.useEffect(() => {
    if (user) {
      user.getIdToken().then(setToken).catch(() => setToken(''));
    }
  }, [user]);

  // Binary role gate — client-side; backend also enforces
  const isRaja = profile?.roleGateway?.selectedRole === 'raja';

  if (!user || !profile) {
    return (
      <div className="rdash-gate">
        <p>Sign in to access the Raja governance dashboard.</p>
      </div>
    );
  }

  if (!isRaja) {
    return (
      <div className="rdash-gate">
        <p>🔒 This dashboard requires the <strong>Raja</strong> governance role.</p>
        <p className="rdash-meta">
          Your current gateway role: <em>{profile.roleGateway?.selectedRole || 'not set'}</em>
        </p>
      </div>
    );
  }

  return (
    <div className="rdash-root">
      <header className="rdash-header">
        <h1 className="rdash-title">राजा शासन — Raja Governance</h1>
        <p className="rdash-subtitle">
          Domain stewardship • Zero automation • Every decision is yours
        </p>
      </header>

      <nav className="rdash-tab-nav" role="tablist">
        {TABS.map(t => (
          <button
            key={t.key}
            role="tab"
            aria-selected={tab === t.key}
            className={`rdash-tab ${tab === t.key ? 'rdash-tab--active' : ''}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </nav>

      <div className="rdash-tab-panel">
        {!token ? (
          <p className="rdash-meta">Loading auth…</p>
        ) : (
          <>
            {tab === 'health'   && <DomainHealthTab   token={token} />}
            {tab === 'queue'    && <ApprovalQueueTab  token={token} />}
            {tab === 'disputes' && <DisputesTab        token={token} />}
            {tab === 'witness'  && <WitnessTab         token={token} />}
            {tab === 'impact'   && <ImpactReportTab    token={token} />}
          </>
        )}
      </div>
    </div>
  );
}
